"use client";

/**
 * Headless UI Dialog + Motion primitives (Animate UI–style transitions).
 * Public product API: `@repo/ui/components/dialog` (Radix-shaped facade).
 */

import {
	CloseButton,
	Dialog as DialogPrimitive,
	DialogBackdrop as DialogBackdropPrimitive,
	DialogPanel as DialogPanelPrimitive,
	DialogTitle as DialogTitlePrimitive,
	Description as DialogDescriptionPrimitive,
} from "@headlessui/react";
import { AnimatePresence, motion, type Transition } from "motion/react";
import type * as React from "react";

type DialogFlipDirection = "top" | "bottom" | "left" | "right";

// Headless UI polymorphic types explode under tsc; keep a thin runtime wrapper.
const DialogPrimitiveAny = DialogPrimitive as unknown as React.ComponentType<
	Record<string, unknown>
>;
const DialogBackdropAny =
	DialogBackdropPrimitive as unknown as React.ComponentType<
		Record<string, unknown>
	>;
const DialogPanelAny = DialogPanelPrimitive as unknown as React.ComponentType<
	Record<string, unknown>
>;
const CloseButtonAny = CloseButton as unknown as React.ComponentType<
	Record<string, unknown>
>;
const DialogTitleAny = DialogTitlePrimitive as unknown as React.ComponentType<
	Record<string, unknown>
>;
const DialogDescriptionAny =
	DialogDescriptionPrimitive as unknown as React.ComponentType<
		Record<string, unknown>
	>;

interface DialogProps {
	open?: boolean;
	onClose: (value: boolean) => void;
	className?: string;
	children?: React.ReactNode;
	autoFocus?: boolean;
	initialFocus?: React.MutableRefObject<HTMLElement | null>;
	role?: "dialog" | "alertdialog";
}

function DialogRoot({ className, ...props }: DialogProps) {
	return (
		<AnimatePresence>
			{props.open ? (
				<DialogPrimitiveAny
					data-slot="dialog"
					className={className}
					{...props}
					static
				/>
			) : null}
		</AnimatePresence>
	);
}

interface DialogBackdropProps {
	className?: string;
	children?: React.ReactNode;
	transition?: Transition;
	as?: React.ElementType;
}

function DialogBackdrop({
	as = motion.div,
	transition = { duration: 0.2, ease: "easeInOut" },
	...rest
}: DialogBackdropProps) {
	return (
		<DialogBackdropAny
			key="dialog-backdrop"
			data-slot="dialog-backdrop"
			as={as}
			initial={{ opacity: 0, filter: "blur(4px)", transition }}
			animate={{ opacity: 1, filter: "blur(0px)", transition }}
			exit={{ opacity: 0, filter: "blur(4px)", transition }}
			{...rest}
		/>
	);
}

interface DialogPanelProps {
	className?: string;
	children?: React.ReactNode | ((bag: unknown) => React.ReactNode);
	from?: DialogFlipDirection;
	transition?: Transition;
	as?: React.ElementType;
	style?: React.CSSProperties;
	ref?: React.Ref<HTMLDivElement>;
}

function DialogPanel({
	children,
	as = motion.div,
	from = "top",
	transition = { type: "spring", stiffness: 150, damping: 25 },
	...rest
}: DialogPanelProps) {
	const initialRotation =
		from === "bottom" || from === "left" ? "20deg" : "-20deg";
	const isVertical = from === "top" || from === "bottom";
	const rotateAxis = isVertical ? "rotateX" : "rotateY";

	return (
		<DialogPanelAny
			key="dialog-panel"
			data-slot="dialog-panel"
			as={as}
			initial={{
				opacity: 0,
				filter: "blur(4px)",
				transform: `perspective(500px) ${rotateAxis}(${initialRotation}) scale(0.8)`,
				transition,
			}}
			animate={{
				opacity: 1,
				filter: "blur(0px)",
				transform: `perspective(500px) ${rotateAxis}(0deg) scale(1)`,
				transition,
			}}
			exit={{
				opacity: 0,
				filter: "blur(4px)",
				transform: `perspective(500px) ${rotateAxis}(${initialRotation}) scale(0.8)`,
				transition,
			}}
			{...rest}
		>
			{(bag: unknown) => (
				<>{typeof children === "function" ? children(bag) : children}</>
			)}
		</DialogPanelAny>
	);
}

interface DialogCloseProps extends Record<string, unknown> {
	as?: React.ElementType;
	className?: string;
	children?: React.ReactNode;
}

function DialogClose({ as = "button", ...rest }: DialogCloseProps) {
	return (
		<CloseButtonAny data-slot="dialog-close" as={as} {...rest} />
	);
}

interface DialogHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
	as?: React.ElementType;
}

function DialogHeader({
	as: Component = "div",
	...props
}: DialogHeaderProps) {
	const Comp = Component as React.ComponentType<Record<string, unknown>>;
	return <Comp data-slot="dialog-header" {...(props as object)} />;
}

interface DialogFooterProps extends React.HTMLAttributes<HTMLDivElement> {
	as?: React.ElementType;
}

function DialogFooter({
	as: Component = "div",
	...props
}: DialogFooterProps) {
	const Comp = Component as React.ComponentType<Record<string, unknown>>;
	return <Comp data-slot="dialog-footer" {...(props as object)} />;
}

interface DialogTitleProps extends React.HTMLAttributes<HTMLHeadingElement> {
	as?: React.ElementType;
	className?: string;
}

function DialogTitle(props: DialogTitleProps) {
	return <DialogTitleAny data-slot="dialog-title" {...props} />;
}

interface DialogDescriptionProps extends React.HTMLAttributes<HTMLElement> {
	as?: React.ElementType;
	className?: string;
}

function DialogDescription(props: DialogDescriptionProps) {
	return (
		<DialogDescriptionAny data-slot="dialog-description" {...props} />
	);
}

export {
	DialogRoot,
	DialogBackdrop,
	DialogPanel,
	DialogClose,
	DialogTitle,
	DialogDescription,
	DialogHeader,
	DialogFooter,
	type DialogProps,
	type DialogBackdropProps,
	type DialogPanelProps,
	type DialogCloseProps,
	type DialogTitleProps,
	type DialogDescriptionProps,
	type DialogHeaderProps,
	type DialogFooterProps,
	type DialogFlipDirection,
};
